/**
 * Auswahlfeld.
 *
 * **Bewusst anders als im Design-System-Projekt.** Dort ist `Select` ein
 * nachgebautes Aufklappmenü aus `<div>`-Elementen mit `onClick`: Es sieht gut
 * aus, ist aber mit der Tastatur nicht bedienbar, meldet einer Sprachausgabe
 * nichts und öffnet auf dem Telefon nicht die native Auswahl.
 *
 * Hier steht deshalb ein echtes `<select>`, das nur so aussieht wie dort —
 * gleiche Höhe, gleicher Rahmen, gleicher Radius, gleicher Pfeil. Ein Nachbau
 * mit vollständiger Tastaturbedienung wäre mehrere Hundert Zeilen, und die erste
 * Ansicht, die ihn wirklich braucht, gibt es noch nicht.
 */
import { useId, useState, type SelectHTMLAttributes } from "react";
import { fontWeight } from "../tokens.js";

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  readonly label: string;
  readonly options: readonly SelectOption[];
  readonly size?: "sm" | "md";
  /** Beschriftung nur für Hilfsmittel sichtbar — für Felder in einer Tabellenzeile. */
  readonly labelHidden?: boolean;
}

/**
 * Der Pfeil als Data-URI: ein Bild weniger, das geladen werden muss.
 *
 * Er kommt aus dem Thema (`--select-chevron`, erzeugt in `emit.ts`), nicht von
 * hier: Ein SVG in einem `background-image` kann keine `var()` auflösen, seine
 * Farbe muss eingebrannt sein — und je Thema eine andere (HM-107).
 */
const CHEVRON = "var(--select-chevron)";

export function Select({
  label,
  options,
  size = "md",
  labelHidden = false,
  disabled = false,
  id,
  style,
  ...rest
}: SelectProps) {
  const [focused, setFocused] = useState(false);
  const generatedId = useId();
  const selectId = id ?? generatedId;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      <label
        htmlFor={selectId}
        style={
          labelHidden
            ? // Sichtbar für Sprachausgaben, unsichtbar auf dem Bildschirm.
              { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }
            : { fontSize: 13, fontWeight: fontWeight.semibold, color: "var(--text-body)" }
        }
      >
        {label}
      </label>

      <select
        id={selectId}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          appearance: "none",
          height: size === "sm" ? 32 : 38,
          padding: "0 32px 0 12px",
          background: `${CHEVRON} no-repeat right 10px center, var(--surface-card)`,
          border: `1px solid ${focused ? "var(--hm-green-500)" : "var(--border-default)"}`,
          borderRadius: "var(--radius-md)",
          boxShadow: focused ? "0 0 0 var(--ring-width) var(--focus-ring)" : "var(--shadow-xs)",
          fontFamily: "var(--font-body)",
          fontSize: size === "sm" ? "var(--field-font-sm)" : "var(--field-font-md)",
          color: "var(--text-strong)",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          outline: "none",
          transition: "border-color .15s ease, box-shadow .15s ease",
        }}
        {...rest}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
